/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { filter } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { action } from '@ember/object';
import AssessmentType from 'linced-app/enums/assessment-type';
import AssessmentDisplay from '@linc-technologies/linced-common/enums/assessment-display';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { restartableTask } from 'ember-concurrency';
import EditAssessment from 'linced-app/mixins/edit-assessment';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class AssessmentsController extends Controller.extend(
  EditAssessment,
) {
  @action
  setCategoryDescription(value) {
    this.categoryDescription = value;
  }

  @action
  setCategoryLabel(value) {
    this.categoryLabel = value;
  }

  @action
  setDeleteLinkedPosts(value) {
    this.deleteLinkedPosts = value;
  }

  // Services
  @service
  permissions;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  AssessmentType = AssessmentType;
  AssessmentDisplay = AssessmentDisplay;
  Scopes = Scopes;
  @tracked
  editingCategory = null;
  @tracked deletingAssessment = null;

  // deleteLinkedPosts specifies weather posts linked to the deleted assessment should be deleted
  // by default they'll be deleted unless user selects otherwise
  @tracked
  deleteLinkedPosts = true;

  @tracked
  assessmentLabel = null;
  @tracked
  deletingCategory = null;
  @tracked
  categoryLabel = '';
  @tracked
  categoryDescription = '';
  @tracked
  parentCategory = null;

  @filter('model.assessments', function (assessment) {
    return isEmpty(assessment.get('category.id'));
  })
  topLevelAssessments;

  // availableSchedules is an array of schedules which are available to the current user.
  @filter('model.gradeschedules', function (schedule) {
    let personId = this.get('session.personId');
    return (
      (schedule.get('isShared') || schedule.get('authorId') === personId) &&
      !schedule.get('isHidden')
    );
  })
  availableSchedules;

  @tracked
  assessmentDialogOpen;

  @tracked
  categoryDialogOpen;

  @tracked
  categoryErrorMessage;

  @tracked
  categorySaveInProgress;

  @tracked
  deleteAssessmentDialogOpen;

  @tracked
  deleteCategoryDialogOpen;
  @tracked assessmentCategory;

  // postsTask loads the posts that link to the assessment. The delete dialog
  // performs it when it opens.
  postsTask = restartableTask(async (assessmentId) =>
    this.store.query('posts/post', { assessmentId }),
  );

  // deletingAssessmentPosts gives the posts of the assessment to delete, or
  // [] until they load. It reads the last run only, which is the run for the
  // assessment that the dialog shows.
  get deletingAssessmentPosts() {
    return this.postsTask.last?.value ?? [];
  }

  get deletingAssessmentPages() {
    return this.getLinkedPages();
  }

  getLinkedPages() {
    return this.model.pages.filter((page) => {
      return page.assessment_ids.includes(this.deletingAssessment.id);
    });
  }

  @action
  noop() {}

  // Category management actions.
  @action
  saveCategory() {
    let category = this.editingCategory;
    let store = this.store;
    this.categorySaveInProgress = true;

    // Create a new Category if needed
    if (isEmpty(category)) {
      category = store.createRecord('assessments/category', {
        schoolId: this.get('session.school'),
        parentCategoryId: this.parentCategory,
      });
    }

    // Set properties from form
    category.setProperties({
      label: this.categoryLabel,
      description: this.categoryDescription,
    });

    category
      .save()
      .then(() => {
        this.categoryDialogOpen = false;
      })
      .catch((err) => {
        // Unload a new record and revert an edited one; the form keeps the input.
        rollbackRecord(category);
        this.categoryErrorMessage = err;
      })
      .then(() => {
        this.categorySaveInProgress = false;
      });
  }

  @action
  toggleCategoryDialog() {
    this.toggleProperty('categoryDialogOpen');
  }

  @action
  newCategory(parent) {
    this.resetCategoryForm();
    this.parentCategory = parent;
    this.categoryDialogOpen = true;
  }

  @action
  editCategory(category) {
    this.resetCategoryForm();
    this.editingCategory = category;
    this.categoryLabel = category.get('label');
    this.categoryDescription = category.get('description');
    this.categoryDialogOpen = true;
  }

  // Assessment management actions.
  @action
  newAssessment(category) {
    this.resetAssessmentForm();
    this.assessmentCategory = category;
    this.assessmentDialogOpen = true;
  }

  @action
  openAssessment(assessment) {
    this.router.transitionTo('admin.assessment', assessment.get('id'));
  }

  @action
  openDeleteAssessmentDialog(assessment) {
    this.deletingAssessment = assessment;
    this.assessmentLabel = assessment.get('label');
    // Delete shows the error when the posts do not load.
    this.postsTask.perform(assessment.get('id')).catch(() => {});
    this.deleteAssessmentDialogOpen = true;
  }

  @action
  async deleteAssessment() {
    try {
      // Wait for the posts before a change, or a failed query leaves the
      // pages unlinked from an assessment that is not deleted.
      let posts = (await this.postsTask.last) ?? [];
      let promises = this.deletingAssessmentPages.map((page) => {
        page.assessment_ids.removeObject(this.deletingAssessment.id);
        return page.save();
      });
      posts.forEach((post) => {
        if (this.deleteLinkedPosts) {
          promises.push(post.destroyRecord());
        } else {
          post.set('assessmentId', null);
          promises.push(post.save());
        }
      });
      promises.push(this.deletingAssessment.destroyRecord());

      await Promise.all(promises);
      this.toaster.success('Assessment deleted successfully.');
    } catch {
      this.toaster.error('Failed to delete assessment.');
    } finally {
      this.deleteAssessmentDialogOpen = false;
    }
  }

  @action
  openDeleteCategoryDialog(category) {
    this.deletingCategory = category;
    this.categoryLabel = category.get('label');
    this.deleteCategoryDialogOpen = true;
  }

  @action
  deleteCategory() {
    this.deletingCategory
      .destroyRecord()
      .then(() => {
        this.toaster.success('Assessment Category deleted successfully.');
      })
      .catch(() => {
        this.toaster.error('Failed to delete Assessment Category.');
      })
      .finally(() => {
        this.deleteCategoryDialogOpen = false;
      });
  }

  // Drag-and-drop sorting actions.
  @action
  moveAssessment({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      // No change in list or position.
      return;
    }

    const item = sourceList[sourceIndex];
    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    const moved = sourceList !== targetList;
    if (moved) {
      // Moved to a new category - update the relationship.
      item.set('categoryId', targetList.__category__);
    }

    // Update target category's assessment sequences, saving the moved
    // assessment once whether or not its sequence changed.
    targetList.forEach((entity, index) => {
      entity.set('sequence', index);
      if (moved && entity === item) {
        entity.save().catch(() => this.revertMove(entity));
      } else if (entity.get('hasDirtyAttributes')) {
        entity.save();
      }
    });
  }

  @action
  moveCategory({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      // No change in list or position.
      return;
    }

    const item = sourceList[sourceIndex];
    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    const moved = sourceList !== targetList;
    if (moved) {
      // Moved to a new parent category - update the relationship.
      item.set('parentCategoryId', targetList.__parent__);
    }

    // Update target category's subcategory sequences, saving the moved
    // category once whether or not its sequence changed.
    targetList.forEach((entity, index) => {
      entity.set('sequence', index);
      if (moved && entity === item) {
        entity.save().catch(() => this.revertMove(entity));
      } else if (entity.get('hasDirtyAttributes')) {
        entity.save();
      }
    });
  }

  // revertMove puts a dragged record back under its old parent when the save
  // fails. The lists are built from the relationships, so it moves back on
  // screen too.
  revertMove(record) {
    rollbackRecord(record);
    this.toaster.error(`Error moving ${record.get('label')}`);
  }

  resetCategoryForm() {
    this.categoryErrorMessage = '';
    this.categoryLabel = '';
    this.categoryDescription = '';
    this.parentCategory = null;
    this.editingCategory = null;
  }
}
